import { Application } from "@hotwired/stimulus";

// Build a lookup map of Stimulus identifier -> Vite import function
const CONTROLLER_FILENAME_REGEX =
  /^(?:.*?(?:controllers|components)\/|\.?\.\/)?(.+)(?:[/_-]controller\..+?)$/;
const CONTROLLER_ATTRIBUTE = "data-controller";

// Get the Vite dynamic import glob
const rawControllers = import.meta.glob("./controllers/**/*.js");

const identifierForGlobKey = (key) => {
  const logicalName = (key.match(CONTROLLER_FILENAME_REGEX) || [])[1];
  if (logicalName) {
    return logicalName.replace(/_/g, "-").replace(/\//g, "--");
  }
};

const controllerMap = {};
for (const path in rawControllers) {
  const identifier = identifierForGlobKey(path);
  if (identifier) {
    controllerMap[identifier] = rawControllers[path];
  }
}

const canRegisterController = (name, app) => !app.router.modulesByIdentifier.has(name);

// Prevent race conditions from registering the same controller multiple times
const loadingControllers = new Set();

const loadController = (name, app) => {
  if (loadingControllers.has(name) || !canRegisterController(name, app)) return;

  const importFn = controllerMap[name];
  if (importFn) {
    loadingControllers.add(name); // Mark as loading
    importFn()
      .then((resModule) => {
        if (canRegisterController(name, app)) {
          const controllerConstructor = resModule.default ?? resModule;
          app.register(name, controllerConstructor);
        }
      })
      .catch((error) => console.error(`Failed to autoload controller: ${name}`, error))
      .finally(() => loadingControllers.delete(name)); // Cleanup once loaded
  }
};

const extractControllerNamesFrom = (element) => {
  const attr = element.getAttribute(CONTROLLER_ATTRIBUTE);
  return attr ? attr.split(/\s+/).filter((content) => content.length) : [];
};

const lazyLoadExistingControllers = (app, element) => {
  // Ensure we check the element itself, not just its children
  if (element.hasAttribute && element.hasAttribute(CONTROLLER_ATTRIBUTE)) {
    extractControllerNamesFrom(element).forEach((name) => loadController(name, app));
  }

  // Use flatMap for slightly better performance and cleaner array flattening
  Array.from(element.querySelectorAll(`[${CONTROLLER_ATTRIBUTE}]`))
    .flatMap(extractControllerNamesFrom)
    .forEach((name) => loadController(name, app));
};

const lazyLoadNewControllers = (app, element) => {
  new MutationObserver((mutationsList) => {
    for (const mutation of mutationsList) {
      switch (mutation.type) {
        case "attributes": {
          if (
            mutation.attributeName === CONTROLLER_ATTRIBUTE &&
            mutation.target.getAttribute(CONTROLLER_ATTRIBUTE)
          ) {
            extractControllerNamesFrom(mutation.target).forEach((name) =>
              loadController(name, app)
            );
          }
          break;
        }
        case "childList": {
          // Only scan newly added nodes, not the entire parent target
          for (const node of mutation.addedNodes) {
            if (node.nodeType === Node.ELEMENT_NODE) {
              lazyLoadExistingControllers(app, node);
            }
          }
          break;
        }
      }
    }
  }).observe(element, { attributeFilter: [CONTROLLER_ATTRIBUTE], subtree: true, childList: true });
};

const startLazyLoading = (app) => {
  lazyLoadExistingControllers(app, document.documentElement);
  lazyLoadNewControllers(app, document.documentElement);
};

// Start app
const application = Application.start();
window.Stimulus = application;
// Start lazy loading
startLazyLoading(application);
